/**
 * Semantic Value to AST Node Converters
 *
 * Converts SemanticValue types to AST expression nodes.
 * Used by the AST builder to construct expression trees from semantic parsing results.
 */

import type {
  SemanticValue,
  LiteralValue,
  SelectorValue,
  ReferenceValue,
  PropertyPathValue,
  ExpressionValue,
  SourcePosition,
} from '../types';

import {
  parseExpression,
  type ExpressionNode,
  type LiteralNode,
  type SelectorNode,
  type AttributeAccessNode,
  type BinaryExpressionNode,
  type IdentifierNode,
  type SelectorKind,
  type TemplateLiteralNode,
} from './expression-parser';

// =============================================================================
// Value Converters
// =============================================================================

/**
 * Convert a SemanticValue to an AST ExpressionNode.
 *
 * @param value - The semantic value to convert
 * @param warnings - Optional array to collect warnings about potentially incorrect type choices
 * @returns The corresponding AST expression node
 */
export function convertValue(value: SemanticValue, warnings?: string[]): ExpressionNode {
  return stampSpan(convertValueShape(value, warnings), value.position);
}

/**
 * Give a converted node the span of the token run its value came from.
 *
 * Only `start`/`end` — the tokenizer records offsets, not line/column, and a
 * value's offsets are relative to whatever string was parsed. A caller that
 * parsed a slice of a larger document owns both the rebasing and the
 * line/column derivation (see {@link SourceSpanned}); `@hyperfixi/core` does
 * exactly that on adoption.
 *
 * A node that already carries a `start` keeps it: {@link convertExpression}
 * has sub-parsed nodes whose spans are finer-grained than the whole role's,
 * and it has already rebased them onto the role's span.
 */
function stampSpan(node: ExpressionNode, position: SourcePosition | undefined): ExpressionNode {
  if (!position || typeof (node as { start?: unknown }).start === 'number') return node;
  return { ...node, start: position.start, end: position.end } as ExpressionNode;
}

function convertValueShape(value: SemanticValue, warnings?: string[]): ExpressionNode {
  switch (value.type) {
    case 'literal':
      // A naked `${…}` URL: core's parser builds it as a template.
      if (value.interpolates && typeof value.value === 'string') {
        const template: TemplateLiteralNode = { type: 'templateLiteral', value: value.value };
        return template;
      }
      return convertLiteral(value);
    case 'selector':
      // `<button/> in me`: core's `in` binary expression.
      if (value.scope) return convertScopedQuery(value, warnings);
      // `[1, 2]` is an array, which the tokenizers read as one attribute-
      // selector token. A bare `[…]` is never a selector in hyperscript (an
      // attribute is `@name` or `[@name=…]`), and querySelectorAll('[1,2]')
      // throws: every translated `repeat for x in [1, 2]`, `log [1, 2]` and
      // `put [1, 2] into x` threw on the direct path.
      if (/^\[(?!@)/.test(value.value)) {
        return convertExpression({ type: 'expression', raw: value.value });
      }
      // `[@title="x"]`, what `add`, `remove` and `toggle` set: core's parser
      // builds the text, which those commands read. As a selector the direct
      // path queried it and changed nothing, in every language.
      if (/^\[@[a-zA-Z_][\w-]*\s*=/.test(value.value)) {
        return { type: 'literal', value: value.value } as LiteralNode;
      }
      return convertSelector(value, warnings);
    case 'reference':
      return convertReference(value);
    case 'property-path':
      return convertPropertyPath(value, warnings);
    case 'expression':
      return convertExpression(value);
    case 'flag':
      // Flags are boolean attributes — convert to a boolean literal
      return { type: 'literal', value: value.enabled } as LiteralNode;
    default:
      // Exhaustive check
      const _exhaustive: never = value;
      throw new Error(`Unknown semantic value type: ${(_exhaustive as SemanticValue).type}`);
  }
}

/**
 * Is this role value a MATERIALIZED DEFAULT rather than something the author wrote?
 *
 * The pattern matcher tags every value it fills in from a schema `default` with
 * `implicit: true` (see `pattern-matcher.ts`'s default materialization) — a
 * bare `focus` gets `patient: {reference me, implicit: true}`, while an authored
 * `focus me` gets the same value WITHOUT the tag. The multilingual renderers
 * already read this to suppress an injected `me` when rendering to 24 languages.
 *
 * The AST builder reads it for the same reason: `args` and `modifiers` are the
 * SYNTAX surface — what the author actually wrote — so a materialized default
 * does not belong there. It stays on `semanticRoles`, the SEMANTICS surface,
 * where a consumer that wants the resolved target can read it.
 */
export function isImplicitValue(value: { implicit?: true } | undefined): boolean {
  return value?.implicit === true;
}

/**
 * Convert a LiteralValue to a LiteralNode.
 */
export function convertLiteral(value: LiteralValue): LiteralNode {
  const result: LiteralNode = {
    type: 'literal',
    value: value.value,
  };

  // Only add dataType if defined (exactOptionalPropertyTypes)
  if (value.dataType) {
    return { ...result, dataType: value.dataType };
  }

  return result;
}

/**
 * Recognize a query reference (`<button/>`, `<.card/>`, `<#id/>`) and return the
 * css inside the brackets.
 *
 * Deliberately narrower than "starts with `<`": the trailing `/>` is what makes
 * the surface a query reference, and core's `isQueryReference` +
 * `queryValue.slice(1, -2)` pair assume it. Returns undefined for anything else.
 */
function matchQueryReference(raw: string): { selector: string } | undefined {
  if (!raw.startsWith('<') || !raw.endsWith('/>') || raw.length <= 3) return undefined;
  const selector = raw.slice(1, -2).trim();
  return selector ? { selector } : undefined;
}

/**
 * Convert a SelectorValue to a SelectorNode.
 *
 * @param value - The selector value to convert
 * @param warnings - Optional array to collect warnings
 */
export function convertSelector(
  value: SelectorValue,
  warnings?: string[]
): SelectorNode | AttributeAccessNode {
  // An `@attr` value is an ATTRIBUTE reference, not a CSS selector — the
  // semantic tokenizer classifies `@disabled` / `@aria-selected` as a selector
  // role value, but feeding it to querySelector throws "Invalid selector".
  // Emit the canonical core-parser shape instead: the runtime reads it via
  // getAttribute, and the write commands (set/toggle) route it to setAttribute.
  if (value.value.startsWith('@') && /^[a-zA-Z_]/.test(value.value.slice(1))) {
    return {
      type: 'attributeAccess',
      attributeName: value.value.slice(1),
    };
  }
  // `[@title]` is the same reference in brackets, and core's parser reads it
  // so: `put [@title] into #out` writes `me`'s title on both engines, where
  // the direct path queried `[title]` and moved the element it found. With a
  // value (`[@title="x"]`, what `add` sets) it stays a selector.
  const bracketed = /^\[@([a-zA-Z_][\w-]*)\]$/.exec(value.value);
  if (bracketed) {
    return { type: 'attributeAccess', attributeName: bracketed[1]! };
  }

  // Warn if selector looks like a CSS property (starts with * followed by a letter/hyphen)
  // This catches cases like "*background-color" which should likely be a literal string
  if (warnings && value.value.startsWith('*') && /^[a-zA-Z-]/.test(value.value.slice(1))) {
    warnings.push(
      `Converted '${value.value}' to a CSS selector, but it looks like a CSS property name. ` +
        `CSS properties in commands like 'transition' should be literal strings, not selectors. ` +
        `Consider using expectedTypes: ['literal'] instead of ['literal', 'selector'] in the command schema.`
    );
  }

  // A QUERY REFERENCE (`<button/>`, `<div.card/>`) is one surface with two
  // meanings, and the core AST resolves the ambiguity with a single shape
  // rather than by knowing the command:
  //
  //   value/selector -> the STRIPPED css (`button`), what querySelectorAll gets
  //   fromQuery      -> true, so the evaluator returns the whole collection
  //                     even for `<#id/>` (upstream QueryRef -> ElementCollection)
  //   raw            -> the full `<…>` markup, which MakeCommand reads FIRST and
  //                     uses to CREATE an element instead of querying it
  //
  // Carrying the markup on `value` too — which this converter used to do — hands
  // `<button/>` straight to the DOM, and `hide <button/>` / `add .x to <button/>`
  // die with `SyntaxError: Invalid selector <button/>` in every language and on
  // every buildAST consumer (core's default English path, the multilingual
  // browser bundles, the R2 execution validator). `raw` is what keeps `make`
  // working, so no command-awareness is needed here. Mirrors
  // `parser.ts`'s `matchQueryReference()` branch exactly.
  const queryRef = matchQueryReference(value.value);
  if (queryRef) {
    return {
      type: 'selector',
      value: queryRef.selector,
      selector: queryRef.selector,
      selectorType: value.selectorKind as SelectorKind,
      fromQuery: true,
      raw: value.value,
    } as SelectorNode;
  }

  // A `<`-prefixed value that is NOT a query reference (a stray `<` or `<=` the
  // tokenizers classify as a selector before reaching their operator list)
  // keeps its verbatim shape — stripping it would corrupt it.
  if (value.value.startsWith('<')) {
    return {
      type: 'selector',
      value: value.value,
      selector: value.value,
      selectorType: value.selectorKind as SelectorKind,
      raw: value.value,
    } as SelectorNode;
  }

  return {
    type: 'selector',
    value: value.value,
    selector: value.value,
    selectorType: value.selectorKind as SelectorKind,
  };
}

/**
 * Convert a ReferenceValue to the identifier the traditional parser emits —
 * scoped for a SIGIL-prefixed variable, plain for everything else.
 *
 * Every reference converges on `identifier` (Thread B item 5, the alias
 * normalisation): the traditional parser emits `identifier{name:'me'}` for
 * `me` / `it` / `you` / `event` / … — measured across every context word —
 * and its evaluator resolves them by name. This function used to emit a
 * dedicated `contextReference` node instead, which forced core's
 * `parser/runtime.ts` to carry a parallel dispatch arm for the same meaning.
 * All 12 node-type divergence sites were executed on both paths first
 * (`node-type-alias-parity.test.ts`) — the shapes behave identically, so this
 * is a spelling change, not a behavioural one.
 *
 * `:count` and `$total` are **variables**, and calling them context references
 * was a live defect, not a spelling difference: `ContextType` is a closed
 * union that never contained them, so `value.value as ContextType` was a
 * lying cast, and core's
 * `evaluateContextReference` has no case for `:count` — it returns `undefined`.
 *
 * The blast radius was larger than it looks, because of WHERE the semantic path
 * gets used. Core parses a command sequence traditionally and hands only the
 * final remainder to the semantic analyzer, so it is the LAST command of a
 * handler that gets this node. Measured on the default parse path:
 *
 *   set :v to 5 then log :v then log :v      →  ["5", "5", undefined]
 *   set $v to 5 then log $v then log $v      →  ["5", "5", undefined]
 *   set  v to 5 then log  v then log  v      →  ["5", "5", "5"]      (unscoped: fine)
 *
 * So any handler ENDING in a `:`/`$` variable read silently produced
 * `undefined` — `on click increment :count then log :count` being the ordinary
 * shape of it. The traditional path was correct throughout.
 *
 * The two spellings below are the traditional parser's, matched exactly so both
 * paths build the same node: `:name` strips the sigil and tags
 * `scope: 'element'`, while `$name` KEEPS its sigil in the name and carries no
 * scope (that is what `getVariableValue` expects of each — not an inconsistency
 * introduced here).
 */
export function convertReference(value: ReferenceValue): IdentifierNode {
  const raw = value.value;

  if (typeof raw === 'string') {
    if (raw.startsWith(':') && raw.length > 1) {
      return { type: 'identifier', name: raw.slice(1), scope: 'element' };
    }
    if (raw.startsWith('$') && raw.length > 1) {
      return { type: 'identifier', name: raw };
    }
  }

  // Possessive surface forms normalise to their base word, matching what the
  // traditional parser's fold emits (`my value` → object `identifier{me}`).
  // Reference values are normally already base words; this is a safety net.
  const POSSESSIVE_BASE: Record<string, string> = { my: 'me', its: 'it', your: 'you' };
  const name = String(raw);
  return { type: 'identifier', name: POSSESSIVE_BASE[name] ?? name };
}

/**
 * Convert a PropertyPathValue to the node the traditional parser emits for
 * the same surface (Thread B item 5, the alias normalisation):
 *
 * - dot access (`event.detail.message`) → a NESTED `memberExpression` chain,
 *   one link per path segment, `property` an identifier node, `computed`
 *   false — the traditional parser's exact shape. This function used to emit
 *   a single flat `propertyAccess` carrying the whole dotted path as a
 *   string, which forced core's evaluator to carry a parallel dot-splitting
 *   arm for the same meaning.
 * - possessive access (`#target's innerHTML`, recorded by the matcher in
 *   `value.access`) → `possessiveExpression`, again the traditional shape.
 *   An unrecorded surface falls back to the memberExpression chain.
 * - EXCEPT a possessive whose object is a pronoun base (`me`/`it`/`you`):
 *   that surface can only be the space form `my`/`its`/`your` + property,
 *   which the traditional parser folds to a memberExpression
 *   (`copy my textContent` → object `identifier{me}`, measured), never to a
 *   possessiveExpression. `event's detail` / `bob's name` keep
 *   possessiveExpression on both paths — also measured.
 *
 * @param value - The property path value to convert
 * @param warnings - Optional array to collect warnings
 */
export function convertPropertyPath(value: PropertyPathValue, warnings?: string[]): ExpressionNode {
  const object = convertValue(value.object, warnings);

  const isPronounBase =
    object.type === 'identifier' && ['me', 'it', 'you'].includes((object as IdentifierNode).name);

  if (value.access === 'possessive' && !isPronounBase) {
    return {
      type: 'possessiveExpression',
      object,
      property: { type: 'identifier', name: value.property },
    } as ExpressionNode;
  }

  let node: ExpressionNode = object;
  for (const part of String(value.property).split('.')) {
    if (!part) continue;
    node = {
      type: 'memberExpression',
      object: node,
      property: { type: 'identifier', name: part },
      computed: false,
    } as ExpressionNode;
  }
  return node;
}

/**
 * Convert an ExpressionValue (raw string) by parsing it with the expression parser.
 * This is the fallback for complex expressions that couldn't be fully parsed
 * at the semantic level.
 */
/**
 * A scoped query (`<button/> in me`) as core's parser writes it: `in`, the
 * query on the left in its query shape (`fromQuery`, which core's evaluator
 * reads to scope the lookup; without it `in` is a containment test and returns
 * a boolean), the scope on the right.
 */
function convertScopedQuery(value: SelectorValue, warnings?: string[]): BinaryExpressionNode {
  const { scope, ...query } = value;
  return {
    type: 'binaryExpression',
    operator: 'in',
    left: convertSelector(query, warnings),
    right: convertValue(scope!, warnings),
  };
}

export function convertExpression(value: ExpressionValue): ExpressionNode {
  const result = parseExpression(value.raw);

  if (!result.success || !result.node) {
    // If parsing fails, return an identifier node with the raw value
    const identifier: IdentifierNode = {
      type: 'identifier',
      name: value.raw,
    };
    return identifier;
  }

  return rebaseSpans(result.node, value.position);
}

/**
 * Rebase a sub-parsed expression tree onto the span of the role it came from.
 *
 * `parseExpression` runs on the role's OWN substring, so every span it emits
 * counts from zero — `clear myVar` reported `args[0]` at `[0, 5)` where the
 * traditional parser reports `[6, 11)`. That is not a missing span but a wrong
 * one, and it leaked all the way to LSP ranges. Adding the role's start offset
 * makes the tree agree with the traditional parse of the same source.
 *
 * `line`/`column` are DROPPED rather than shifted: they described a position
 * inside the substring, and recovering the real ones needs the full document,
 * which this package never sees. Absent beats wrong — and the one caller that
 * does hold the document (`@hyperfixi/core`) derives them from the rebased
 * offsets.
 */
function rebaseSpans(node: ExpressionNode, position: SourcePosition | undefined): ExpressionNode {
  if (!position || position.start === 0) return node;
  const shift = (input: unknown): unknown => {
    if (Array.isArray(input)) return input.map(shift);
    if (!input || typeof input !== 'object') return input;
    // Plain objects and arrays are rebuilt; anything else passes by reference,
    // so a generic walk cannot flatten a `Map` field into `{}`.
    const proto = Object.getPrototypeOf(input) as unknown;
    if (proto !== Object.prototype && proto !== null) return input;
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(input)) {
      if ((key === 'start' || key === 'end') && typeof v === 'number') {
        out[key] = v + position.start;
      } else if (key === 'line' || key === 'column') {
        continue;
      } else {
        out[key] = shift(v);
      }
    }
    return out;
  };
  return shift(node) as ExpressionNode;
}

// =============================================================================
// Type Guards
// =============================================================================

export function isLiteralValue(value: SemanticValue): value is LiteralValue {
  return value.type === 'literal';
}

export function isSelectorValue(value: SemanticValue): value is SelectorValue {
  return value.type === 'selector';
}

export function isReferenceValue(value: SemanticValue): value is ReferenceValue {
  return value.type === 'reference';
}

export function isPropertyPathValue(value: SemanticValue): value is PropertyPathValue {
  return value.type === 'property-path';
}

export function isExpressionValue(value: SemanticValue): value is ExpressionValue {
  return value.type === 'expression';
}
